/* eslint-disable camelcase */

import { ArchiveDeleteResponse } from "components/common/ArchiveDialog/ArchiveDialog";
import { ApplyFilters, Filter } from "lib/utils/filterUtils";
import { SWRConfiguration } from "swr";
import { ApiResponse, ServerResponse } from "./axiosConfig";
import { useSwr, axiosInst } from "./axiosHVS";
import { Attribute } from "./hvsBase";

export interface EmailActionAttributes {
  email_template_id: string;
  use_html: string; // true/false
  to: string; // "62f1261ddcd43c0001e50b55;62f21087da5e6b00019931f0;62f255a7da5e6b0001993f4c;moreno.mazzoni@notexist.com;anita.blake",
  cc: string;
  bcc: string;
}
export interface WorkflowCondition {
  property?: string;
  type?: string;
  operator?: string;
  value?: string | number | unknown;
  groupId: number;
  groupLogic: string;
  rangevalues?: string[];
  displayLabel?: string;
}

export interface IncidentData {
  incidentTypeId: number;
  fk_priority_inc_id: number;
  fk_severity_inc_id: number;
  inc_nearyby_dist_in_miles?: number;
  inc_occ_dur_in_secs?: number;
  nearest_cameras_dist_in_miles?: number;
  inc_watchlist?: User[];
  inc_assigned_user?: User;
}
export interface CreateIncidentActionAttribute {
  value: string;
}

export interface WorkflowActionData extends Record<string, unknown> {
  id?: string;
  name?: string;
  label?: string;
  continueOnError: boolean;
  continueOnNoResult: boolean;
  exe_order: number;
  notification_template_id?: string;
  incident_data?: IncidentData;
  attributes: Record<string, Attribute | CreateIncidentActionAttribute>;
  isInvalid: boolean;
}

/* export interface Workflow extends Record<string, unknown> {
  id: string;
  pk_wf_template_id: number;
  wf_name: string;
  wf_desc: string;
  wf_exec_value: string;
  hv_entity_type_id: string;
  wf_conditions: Array<WorkflowCondition>;
  wf_rule: string;
  wf_distance_rule: Array<string>;
  wf_status_value: string;
  wf_action: WorkflowAction[];
} */
export interface RuleDto extends Record<string, unknown> {
  id: string;
  /// Entity id of the selected entity type, this filed is the field id into collection entity_type
  type_id: string;
  /// Identify when a rule must be executed when an event action happen.
  /// Possible trigger:
  /// 0 → Always
  /// 1 → Event creation
  trigger: number;
  /// The Workflow status, could be:
  /// 0 → Draft
  /// 1 → Active
  /// 2 → Inactive
  /// 3 → Template
  status: number;
  name: string;
  description: string;
  created_by?: string;
  update_by?: string;
  domain_id?: string;
  rule_version?: string;
  priority: number;
  input_parameters: RuleInputParameterDto;
  template: RuleTemplateDto;
  schedule: RuleScheduleDto;
}
export interface RuleScheduleDto {
  /// If true means that the endpoint must create a scheduled task to be executed at “time”.
  is_scheduled: boolean;
  /// H24 time when execute the workflow.
  /// This is always the first time set, depending from others parameters this value can be different means.
  time: string;

  /// Days when workflow must be executed. Workflow will be executed required days at time.
  days: string;

  /// Interval in minutes between workflow execution, task will start first time at time and will be executed at regular interval.
  interval_minute: string;

  /// Workflow execution frequency could be:
  /// interval: execution depends on interval_minute
  /// days: execution will be specifics days at time
  /// hourly: execution will be happen each 60 minutes starting from time
  /// daily: execution will be each day at time
  /// weekly: execution will be one days of the week at time.
  frequency: string;
}
export interface RuleActionDto {
  /// The rule engine action identifier.
  type: string;
  /// Action priority respect to other action inside the workflow.
  priority: number;
  /// The action parameters, what types and number depends on action.
  parameters: { [key: string]: string } | EmailActionAttributes;
}
export interface RuleTemplateDto {
  /// If true the status must be set to 3 and the field name come mandatory.
  is_template: boolean;
  /// Template name used when is_template is true.
  name: string;
  description: string;
}
export interface RuleInputParameterDto {
  conditions_group: RuleConditionsGroupDto[];
  actions: RuleActionDto[];
}
export interface RuleConditionsGroupDto {
  /// This value can be and/or and is used for evaluation between groups, when only one group it’s available could be empty (not null).
  /// For example if we have Group1 and Group2, this operator will be used to test Group1 exit Condition with Group2 exit condition.
  /// The exit condition is the result from internal condition.
  global_operator: string;
  /// The Used to identify a group, it’s optional and will be only displayed on UI.
  /// </value>
  group_name: string;
  /// It’s a progressive number that identify the group inside the conditions.
  /// It’s Used from Ui to order the conditions groups
  group_order: number;
  conditions: RuleConditionDto[];
}
export interface RuleConditionDto {
  /// This value can be and/or and is used to evaluate conditions with next condition object. In the last condition this attribute is empty.
  local_operator: string;
  /// It’s the attribute name inside the “attributes” attribute list of the entity type referenced from the workflow.
  property: string;
  /// The Attribute name displayed from the UI
  display_label: string;
  /// The Lowercase property type, supported type are:
  /// distance
  /// text
  /// textarea
  /// numeric
  /// datetime
  type: string;
  /// The rule engine function to execute. This list is part of the Rule engine condition library.
  function: string;
  /// The a list with all parameters need to the function.
  fields: string[];
}

export interface RuleSearchResult {
  results?: RuleSearchDetail[];
  filters?: Filter[];
}
export interface RuleSearchDetail extends Record<string, unknown> {
  _id: string;
  priority: number;
  name: string;
  description: string;
  status: number;
  created_by_name: string;
}
export enum RuleStatus {
  Active = 1,
  Inactive = 2,
}
export enum RuleTriggerValues {
  Creation = 1,
  Always = 0,
}
const baseKey = "/rules";

export const useWorkflows = (
  params: {
    pageNr?: number;
    pageSize?: number;
    orderBy?: string;
    order?: string;
    searchKey?: string;
    calculateFilters?: boolean;
  } = {
    pageNr: 1,
    pageSize: 10,
    orderBy: undefined,
    order: "desc",
    searchKey: undefined,
    calculateFilters: true,
  },
  config?: SWRConfiguration,
  filters?: ApplyFilters[]
): ServerResponse<ApiResponse<RuleSearchResult>> => {
  const urlKey = "/bulk/rules/get";
  return useSwr<ApiResponse<RuleSearchResult>>(
    `${urlKey}?PageNr=${params.pageNr}&PageSize=${params.pageSize}${
      params.orderBy != null
        ? `&SortName=${params.orderBy}&SortOrder=${params.order}`
        : ""
    }${params.searchKey != null ? `&Keyword=${params.searchKey}` : ""}`,
    config,
    filters
  );
};

export const useWorkflow = (
  id?: string,
  config?: SWRConfiguration
): ServerResponse<ApiResponse<RuleDto>> =>
  useSwr<ApiResponse<RuleDto>>(`${baseKey}/${id}`, config);

export const addWorkflow = async (data: Partial<RuleDto>): Promise<unknown> => {
  const res = await axiosInst.post(`${baseKey}`, data);
  return res.data;
};

export const getWorkflow = async (
  id?: string
): Promise<ApiResponse<RuleDto>> => {
  const res = await axiosInst.get(`${baseKey}/${id}`);
  return res.data;
};

export const editWorkflow = async (
  data: Partial<RuleDto>
): Promise<ApiResponse<RuleDto>> => {
  const res = await axiosInst.put(`${baseKey}/${data.id}`, data);
  return res.data;
};
export const deleteAllWorkflow = async (
  id: string[]
): Promise<ArchiveDeleteResponse<string>> => {
  const url = "/bulk/rules/delete";
  const res = await axiosInst.post(url, id);
  return res.data;
};
